import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Send } from 'lucide-react';
import { cn } from '@/lib/cn';
import { onFocusChatComposer } from '@/hooks/useKeyboardShortcuts';
import { matchSlashCommands } from './slash-commands';

type Props = {
  onSubmit: (text: string) => void;
  disabled?: boolean;
};

export function ChatComposer({ onSubmit, disabled }: Props) {
  const [value, setValue] = useState('');
  const [selected, setSelected] = useState(0);
  const ref = useRef<HTMLTextAreaElement>(null);
  const matches = useMemo(() => matchSlashCommands(value), [value]);
  const menuOpen = matches.length > 0;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 180) + 'px';
  }, [value]);

  useEffect(() => onFocusChatComposer(() => ref.current?.focus()), []);

  useEffect(() => {
    setSelected((prev) => (matches.length === 0 ? 0 : Math.min(prev, matches.length - 1)));
  }, [matches.length]);

  const submit = () => {
    const text = value.trim();
    if (!text || disabled) return;
    onSubmit(text);
    setValue('');
  };

  const submitSlash = (slashName: string) => {
    if (disabled) return;
    onSubmit(slashName);
    setValue('');
    ref.current?.focus();
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (menuOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected((i) => (i + 1) % matches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected((i) => (i - 1 + matches.length) % matches.length);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setValue('');
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        setValue(matches[selected].name);
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        submitSlash(matches[selected].name);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="border-t border-border p-3">
      <div className="relative flex items-end gap-2 rounded-md border border-border bg-background px-2 py-1.5 focus-within:border-ring">
        {menuOpen && (
          <div className="absolute inset-x-1 bottom-full z-10 mb-2 overflow-hidden rounded-md border border-border bg-popover shadow-md">
            {matches.map((cmd, i) => (
              <button
                key={cmd.name}
                type="button"
                role="option"
                aria-selected={i === selected}
                className={cn(
                  'block w-full border-b border-border px-2 py-1.5 text-left last:border-b-0',
                  i === selected ? 'bg-accent text-accent-foreground' : 'hover:bg-muted/60',
                )}
                onMouseDown={(e) => {
                  e.preventDefault();
                  submitSlash(cmd.name);
                }}
                onMouseEnter={() => setSelected(i)}
              >
                <div className="text-xs font-medium">{cmd.name}</div>
                <div className="text-[11px] text-muted-foreground">{cmd.description}</div>
              </button>
            ))}
          </div>
        )}
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKey}
          rows={1}
          placeholder="Ask about the map… (Shift+Enter for newline)"
          className="min-h-[24px] flex-1 resize-none bg-transparent text-sm leading-6 outline-none placeholder:text-muted-foreground"
          disabled={disabled}
        />
        <button
          type="button"
          onClick={submit}
          disabled={disabled || !value.trim()}
          className={cn(
            'grid size-7 shrink-0 place-items-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            'disabled:cursor-not-allowed disabled:opacity-40',
          )}
          aria-label="Send"
          title="Send (Enter)"
        >
          <Send className="size-3.5" />
        </button>
      </div>
    </div>
  );
}
